import type { ReactNode } from "react";
import {
  Box, Divider, HStack, Popover, PopoverContent, PopoverTrigger, Portal, Wrap, WrapItem, VStack,
} from "@chakra-ui/react";
import { Link as RouterLink, generatePath } from "react-router";
import { FiArrowUpRight } from "react-icons/fi";
import CDS from "design-system";
import CaminoTaskIcon from "design-system/icons/Task";
import RoleLabelTag from "components/Tag/UserTag/RoleLabelTag";
import IconTile from "features/Path/Components/PathsList/IconTile";
import { PEOPLE_TEAM_ROUTES } from "definitions/constants/routeConstants";
import type { GridTaskTemplate, GridUser } from "../types";
import HoverCardTimeline from "./TaskTemplateHoverCard/Timeline";

interface Props {
  template: GridTaskTemplate;
  role: string | null;
  assigneeUser?: GridUser | null;
  descriptors: { start: string | null; due: string | null };
  children: ReactNode;
}

const TASK_ACTION_LABEL: Record<string, string> = {
  assign_buddy: "Assign Buddy",
  create_email_account: "Add Work Email",
  create_onboarding_plan: "Add Onboarding Plan",
  send_message: "Message Prompt",
};

export default function TaskTemplateHoverCard({ template, role, assigneeUser, descriptors, children }: Props) {
  const hasTimeline = Boolean(descriptors.start || descriptors.due);
  const taskActionLabel = template.taskAction ? TASK_ACTION_LABEL[template.taskAction] ?? null : null;
  const hasMeta = Boolean(role) || Boolean(assigneeUser) || Boolean(taskActionLabel);
  const paths = template.paths ?? [];
  const hasPaths = paths.length > 0;

  return (
    <Popover
      trigger="hover"
      placement="right-start"
      openDelay={450}
      closeDelay={150}
      isLazy
      closeOnBlur
      closeOnEsc
      gutter={8}
    >
      <PopoverTrigger>{children}</PopoverTrigger>
      <Portal>
        <PopoverContent
          role="tooltip"
          width="320px"
          bg="white"
          border="1px solid"
          borderColor="gray.200"
          boxShadow="md"
          borderRadius="lg"
          p={4}
          _focus={{ outline: "none", boxShadow: "md" }}
        >
          <VStack align="stretch" spacing={3}>
            <HStack align="center" spacing={2.5}>
              <Box color="blue.500" flexShrink={0} display="flex" alignItems="center">
                <CaminoTaskIcon boxSize="18px" />
              </Box>
              <CDS.Text16 fontWeight="semibold" color="fg.default" lineHeight="1.3">
                {template.name}
              </CDS.Text16>
            </HStack>

            {hasMeta && (
              <HStack spacing={2} align="center">
                {(role || assigneeUser) && <RoleLabelTag role={role} user={assigneeUser} size="sm" />}
                {taskActionLabel && (
                  <CDS.Text12 color="gray.500">{taskActionLabel}</CDS.Text12>
                )}
              </HStack>
            )}

            {hasTimeline && (
              <>
                <Divider borderColor="gray.100" />
                <Box px={2} pt={1} pb={1}>
                  <HoverCardTimeline startLabel={descriptors.start} dueLabel={descriptors.due} />
                </Box>
              </>
            )}

            {hasPaths && (
              <>
                <Divider borderColor="gray.100" />
                <VStack align="stretch" spacing={1.5}>
                  <CDS.Text10 fontWeight="medium" color="gray.500" textTransform="uppercase" letterSpacing="0.04em">
                    Paths
                  </CDS.Text10>
                  <Wrap spacing={1.5}>
                    {paths.map((p) => (
                      <WrapItem key={p.id}>
                        <HStack
                          spacing={1.5}
                          bg="gray.50"
                          borderRadius="md"
                          pl={1}
                          pr={2}
                          py={1}
                        >
                          <IconTile icon={p.icon} color={p.iconColor} size={18} />
                          <CDS.Text12 color="fg.default">{p.name}</CDS.Text12>
                        </HStack>
                      </WrapItem>
                    ))}
                  </Wrap>
                </VStack>
              </>
            )}

            <Divider borderColor="gray.100" />
            <HStack
              as={RouterLink}
              to={generatePath(PEOPLE_TEAM_ROUTES.taskTemplates.show, { id: String(template.id) })}
              spacing={1}
              align="center"
              color="blue.600"
              _hover={{ textDecoration: "underline" }}
            >
              <CDS.Text12 fontWeight="medium">View full</CDS.Text12>
              <Box as={FiArrowUpRight} boxSize="14px" />
            </HStack>
          </VStack>
        </PopoverContent>
      </Portal>
    </Popover>
  );
}
